/**
 * Membership Service
 * 
 * API service layer for membership-specific operations.
 * 
 * Handles:
 * - Creating user memberships (purchase)
 * - Fetching membership details
 * 
 * Note: For fetching available memberships in the purchase flow, 
 * use `subscriptionService.getAvailableSubscriptions()` which returns
 * both memberships and passes in a single call.
 * 
 * @module subscription/api
 */

import { apiClient } from '~/lib/apiClient';
import { appRuntime } from '~/services/appRuntime';
import type { ApiResult } from '~/lib/apiClient/types';
import type { ApiMembership } from '@lgc/database/types/domains/membership';
import type { CreateUserMembershipRequest, CreateUserMembershipResponse } from '../types';

/**
 * Membership Service
 *
 * Handles all membership-specific API operations
 */
export const membershipService = {

  /**
   * Fetch ONE membership by id.
   *
   * Exists for the `?plan=` deep link. `getAvailableSubscriptions()` returns only
   * `public` plans, so an UNLISTED plan can never be found there — this endpoint
   * serves `public` and `unlisted` alike and 404s the rest, which is what makes a
   * shared link work at all.
   *
   * (These by-id helpers were deleted as dead code in the public-API cleanup;
   * they are back because unlisted plans gave them a real consumer.)
   *
   * A restricted or missing plan is a 404 either way — deliberately
   * indistinguishable, so the id space says nothing about what exists.
   */
  async getMembershipDetails(membershipId: number): Promise<ApiResult<{ membership: ApiMembership }>> {
    const tenantHandle = appRuntime.tenantHandle;

    return await apiClient.get<{ membership: ApiMembership }>(
      `/memberships/details/${membershipId}`,
      { tenantHandle: tenantHandle || undefined },
    );
  },
  /**
   * Create a user membership (purchase)
   * 
   * Creates a new user membership after payment processing.
   * 
   * @param request - Membership purchase request payload
   * @returns ApiResult with created user membership
   * 
   * @example
   * ```typescript
   * const result = await membershipService.createUserMembership({
   *   userId: 123,
   *   membershipId: 456,
   *   clubId: 2,
   *   paymentMethodId: 'pm_123456',
   * });
   * 
   * if (result.success) {
   *   console.log('User membership created:', result.payload.userMembershipId);
   * }
   * ```
   */
  async createUserMembership(
    request: CreateUserMembershipRequest
  ): Promise<ApiResult<CreateUserMembershipResponse>> {
    const tenantHandle = appRuntime.tenantHandle;
    
    return await apiClient.post<CreateUserMembershipResponse>(
      '/memberships/user',
      request,
      {
        tenantHandle: tenantHandle || undefined,
      }
    );
  },
};
